import { actions as DocumentActions } from '~app/features/document';
import * as htmlToImage from 'html-to-image';
import { CANVAS_SIZE } from '~app/features/canvas/consts';
import { actions as GradingActions } from '~app/features/gradingTool';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as SaveLoadActions } from '~app/features/saveLoad';

const graphModelMap = {
  graph: 'graph',
  conceptual: 'conceptual',
  causal: 'causal',
};

// if there is a text specific to model type for a column, add in this map
const modelSpecificTextMap = {
  missingConnectors: {
    graph:
      'List of connectors that are missing between two objects. (+ve) identifies missing positive connectors and (-ve) identifies missing negative connectors.',
    conceptual: 'List of connectors that are missing between two objects',
    causal:
      'List of connectors that are missing between two objects. (+ve) identifies missing positive connectors and (-ve) identifies missing negative connectors.',
    stockflow:
      'List of connectors that are missing between two objects. (+ve) identifies missing positive connectors and (-ve) identifies missing negative connectors.',
  },
};

export const gradeSubmissions = submissions => async (dispatch, getState) => {
  const modelType = getState().document.present.type;
  const divideByZeroMessage = 'No points possible. All objects are decoys.';
  const resultsList = graphModelMap[modelType]
    ? [
        {
          Personal_Identifier: '',
          Model_Name: '',
          Save_Timestamp: '',
          Student_Score:
            'Student_Score = 100 * Student_Total_Points / Total_Points_Possible',
          Student_Total_Points: 'Student_Total_Points = Connectors_Score',
          Total_Points_Possible:
            'Conceptual Models Total Points Possible = Connectors_Score;',
          Student_Model_Filename:
            'PNG of Student Model that includes red dots of incorrect model attributes. Unique numbers are appended to each filename.',
          Connectors_Score:
            'Each connector is worth 1 point. Connectors_Score = Number of Correct Connectors - Incorrect Connectors. In order to receive 1 point for a connector ALL three of the following conditions must be satisfied: 1) The connector must link the correct two objects. 2) The connector must show the correct direction of causation (For example, X -> Y, not Y -> X). This does not apply for neutral connectors. 3) The connector must be the correct connector type (positive, negative, or neutral). Incorrect connectors will subtract 1 point from the Student_Total_Points.',
          Incorrect_Connectors:
            'List of connectors that are incorrectly connecting two objects',
          Missing_Connectors: modelSpecificTextMap.missingConnectors[modelType],
          uuid: '',
        },
      ]
    : [
        {
          Personal_Identifier: '',
          Model_Name: '',
          Save_Timestamp: '',
          Student_Score:
            'Student_Score = 100 * Student_Total_Points / Total_Points_Possible',
          Student_Total_Points:
            'Student_Total_Points = Connectors_Score + Objects_Score + Flow_Links_Score',
          Total_Points_Possible:
            'Stock-and-Flow Models Total Points Possible =  Connectors_Score + Objects_Score + Flow_Links_Score',
          Student_Model_Filename:
            'PNG of Student Model that includes red dots of incorrect model attributes. Unique numbers are appended to each filename.',
          Objects_Score:
            'Stock-and-Flow Models ONLY. Objects receive 1 point each for being assigned the correct Object Type. Objects_Score = Number of objects assigned to the correct Object Type (Stock, Flow, or Variable). If an object is not supposed to be assigned an Object Type (for example, an object distractor), then it will not be graded unless it is erroneously connected to the model, in which case it will deduct a point.',
          Flow_Links_Score:
            "Stock-and-Flow Models ONLY. The 'Flow_to' is worth 1 point and the 'Flow_from' is worth 1 point. Therefore each flow is worth 2 points. If a model has multiple flows, each flow will be scored by assessing the connection of the 'Flow_to' and the connection of the 'Flow_from'. Some open system models may not have the 'Flow_to' or the 'Flow_from' connected and that is how the model will be assessed. Flow_Links_Score = Number of Correct 'Flow_to' + Number of Correct 'Flow_from'",
          Connectors_Score:
            'Each connector is worth 1 point. Connectors_Score = Number of Correct Connectors - Incorrect Connectors. In order to receive 1 point for a connector ALL three of the following conditions must be satisfied: 1) The connector must link the correct two objects. 2) The connector must show the correct direction of causation (For example, X -> Y, not Y -> X). This does not apply for neutral connectors. 3) The connector must be the correct connector type (positive, negative, or neutral). Incorrect connectors will subtract 1 point from the Student_Total_Points.',
          Incorrect_Object_Types:
            'List of objects that students assigned to the incorrect Object Type',
          Incorrect_Flow_Links: 'List of flows that are linked incorrectly',
          Incorrect_Connectors:
            'List of connectors that are incorrectly connecting two objects',
          Missing_Connectors: modelSpecificTextMap.missingConnectors[modelType],
          uuid: '',
        },
      ];
  const arrImageBlobs = [];

  const wait = ms => {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  };

  for (const [i, submission] of submissions.entries()) {
    const { workingModel, name, creator, savedDateTime, uuid } = submission;

    dispatch(DocumentActions.loadWorkingModel(workingModel));
    // let current call stack complete before continuing
    // to ensure model elements get properly loaded and positioned
    await wait(0);
    dispatch(DocumentActions.tabulateErrors());

    const workingModelErrors = getState().document.present.workingModelErrors;

    const {
      objectMistakes,
      flowFromMistakes,
      flowToMistakes,
      objectMistakeCount,
      missingObjectsCount,
      flowMistakeCount,
      connectorMistakeCount,
      missingConnectorCount,
      solutionSignalCount,
      connectorMistakes,
      missingConnectors,
    } = workingModelErrors;

    const solutionModelTotalObjectsCount = getState().document.present
      .solutionModelTotalObjectsCount;
    const solutionModelTotalFlowObjectsCount = getState().document.present
      .solutionModelTotalFlowObjectsCount;

    const maxPointsPossible = graphModelMap[modelType]
      ? solutionSignalCount
      : solutionModelTotalObjectsCount +
        2 * solutionModelTotalFlowObjectsCount +
        solutionSignalCount;
    const objectsScore = graphModelMap[modelType]
      ? ''
      : solutionModelTotalObjectsCount -
        objectMistakeCount -
        missingObjectsCount;
    const flowLinksScore = graphModelMap[modelType]
      ? ''
      : 2 * solutionModelTotalFlowObjectsCount - flowMistakeCount;
    const connectorScore =
      solutionSignalCount - connectorMistakeCount - missingConnectorCount;
    const incorrectFlowFromLinks =
      flowFromMistakes.join(' flow from, ') +
      (flowFromMistakes.length ? ' flow from. ' : '');
    const incorrectFlowToLinks =
      flowToMistakes.join(' flow to, ') +
      (flowToMistakes.length ? ' flow to. ' : '');
    const incorrectFlowLinks = incorrectFlowFromLinks + incorrectFlowToLinks;
    let totalStudentPoints = graphModelMap[modelType]
      ? connectorScore
      : objectsScore + flowLinksScore + connectorScore;
    totalStudentPoints = totalStudentPoints > 0 ? totalStudentPoints : 0; //student can't score less than zero
    const studentModelFileName = `${name
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/ /g, '_')}_${creator}_${SaveLoadActions.getTimeStamp(
      +savedDateTime,
    )}_${i}.png`;

    graphModelMap[modelType]
      ? resultsList.push({
          Personal_Identifier: creator,
          Model_Name: name,
          Save_Timestamp: new Date(+savedDateTime).toLocaleString(),
          Student_Score: maxPointsPossible
            ? Math.round((100 * totalStudentPoints) / maxPointsPossible)
            : divideByZeroMessage,
          Student_Total_Points: totalStudentPoints,
          Total_Points_Possible: maxPointsPossible,
          Student_Model_Filename: studentModelFileName,
          Connectors_Score: connectorScore,
          Incorrect_Connectors: connectorMistakes.join(', '),
          Missing_Connectors: missingConnectors.join(', '),
          uuid: uuid,
        })
      : resultsList.push({
          Personal_Identifier: creator,
          Model_Name: name,
          Save_Timestamp: new Date(+savedDateTime).toLocaleString(),
          Student_Score: maxPointsPossible
            ? Math.round((100 * totalStudentPoints) / maxPointsPossible)
            : divideByZeroMessage,
          Student_Total_Points: totalStudentPoints,
          Total_Points_Possible: maxPointsPossible,
          Student_Model_Filename: studentModelFileName,
          Objects_Score: objectsScore,
          Flow_Links_Score: flowLinksScore,
          Connectors_Score: connectorScore,
          Incorrect_Object_Types: objectMistakes.join(', '),
          Incorrect_Flow_Links: incorrectFlowLinks,
          Incorrect_Connectors: connectorMistakes.join(', '),
          Missing_Connectors: missingConnectors.join(', '),
          uuid: uuid,
        });

    dispatch(CanvasActions.zoomToDefault());

    await htmlToImage
      .toBlob(document.getElementById('capture'), {
        backgroundColor: 'white',
        width: CANVAS_SIZE,
        height: CANVAS_SIZE,
        pixelRatio: 1,
      })
      .then(blob => {
        arrImageBlobs.push({
          filename: studentModelFileName,
          blob: URL.createObjectURL(blob),
        });
      });
  }

  dispatch(GradingActions.setGradingResults(resultsList));
  dispatch(GradingActions.setMarkedScreenshots(arrImageBlobs));
};
